// Article « ASSR 2 — Comment bien la préparer en 3ᵉ »
// Guide permanent à destination des élèves de 3ᵉ, parents et professeurs EMC.
// Pont pédagogique : ASSR 2 → Code (ETM) → Permis A1, A2, B.

import {
  GraduationCap, School, Users, CheckCircle2, AlertTriangle,
  BookOpen, Clock, ShieldCheck, Bike, Lightbulb, HeartPulse,
  Eye, Target, Award, Scale, Car, Wine,
} from 'lucide-react';

export const assr2Article = {
  slug: 'assr2-preparer-3eme',
  weekVolume: null,
  weekYear: 2026,
  monthLabel: 'Guide permanent',
  category: 'ASSR & scolarité',
  isGuide: true,
  shortTitle: "ASSR 2 — Préparer en 3ᵉ",
  title: "ASSR 2 : comment bien la préparer en classe de 3ᵉ",
  kicker: 'Guide élève, parent, professeur',
  subtitle:
    "L'ASSR 2, c'est LA porte d'entrée au permis de conduire (voiture, moto, poids lourd). Passée en 3ᵉ, elle reste valable toute ta vie. Voici comment la réussir et comprendre son rôle dans ton parcours.",
  heroImage: '/images/articles/assr-moto-rouge-pluie.jpg?v=20260514',
  heroImageAlt:
    "Moto sportive rouge sous la pluie en ville la nuit — symbole de la transition vers le deux-roues motorisé après l'ASSR 2",
  topRightKicker: { line1: 'Guide scolaire', line2: '3ᵉ · ≈ 15 ans' },
  readTimeMin: 7,
  seoDescription:
    "ASSR 2 en 3ᵉ : contenu de l'examen, préparation, lien avec le Code moto (ETM) et le permis A1/A2/B, entraînement officiel gratuit. Guide ECSR pour élèves, parents et profs.",
  seoKeywords:
    "ASSR2, ASSR 2, Attestation Sécurité Routière 3ème, permis moto 15 ans, A1 A2, Code moto ETM, préparer ASSR2, éducation sécurité routière, entraînement ASSR",
  about:
    "Préparation à l'Attestation Scolaire de Sécurité Routière niveau 2",
  source: {
    label: 'Ministère de l\'Éducation nationale — e-ASSR officiel',
    url: 'https://e-assr.education-securite-routiere.fr/preparer/assr/2/ASSR2',
    refs: "Arrêté du 25 mars 2007 relatif aux ASSR et à l'ASR ; Code de la route (art. R211-1).",
  },

  chapo: {
    kicker: 'Pourquoi cet article',
    body:
      "L'<strong>ASSR 2</strong> est sans doute l'attestation <strong>la plus importante de ta scolarité</strong>. Elle se passe en fin de 3ᵉ et conditionne l'accès à <strong>tous les permis</strong> pour les personnes nées après 1987 : voiture (B), moto (A1, A2), poids lourd. Sans elle (ou son équivalent, l'ASR), impossible de s'inscrire à un permis. Bonne nouvelle : elle est 100% gratuite à préparer, et une fois obtenue, elle est <strong>valable à vie</strong>.",
    sideStat: {
      kicker: 'Obligatoire',
      big: '100%',
      body:
        "des candidats au permis de conduire nés <strong>après le 31 décembre 1987</strong> doivent présenter l'ASSR 2 (ou ASR) pour leur <strong>première inscription</strong>. Sans ce document, l'inscription est refusée.",
      accent: '#16a34a',
    },
  },

  pullQuote: {
    text:
      "L'ASSR 2 n'est pas juste un papier. C'est le moment où tu passes du statut d'enfant sur la route à celui de futur conducteur responsable.",
    source: 'Moniteurs ECSR — éducation à la route',
  },

  sections: [
    {
      part: 'Partie 1 — Pourquoi elle compte',
      title: "Le vrai rôle de l'ASSR 2",
      intro:
        "L'ASSR 2 n'est pas qu'une formalité. C'est le <strong>sas d'entrée vers l'autonomie routière</strong> — et elle t'accompagne pour la vie.",
      cards: [
        {
          icon: Car,
          accent: '#16a34a',
          kicker: 'Permis voiture (B)',
          title: "Obligatoire à 17-18 ans",
          body:
            "Pour t'inscrire en conduite accompagnée (AAC, dès 15 ans) ou au permis B classique (17-18 ans), tu dois présenter ton <strong>ASSR 2 originale</strong>. Sans elle : inscription refusée.",
        },
        {
          icon: Bike,
          accent: '#cf2031',
          kicker: 'Permis moto',
          title: "A1 dès 16 ans, A2 dès 18 ans",
          body:
            "Le <strong>Code moto (ETM)</strong>, puis la formation <strong>plateau + circulation</strong> : tout démarre avec l'ASSR 2. Elle est exigée pour A1 (125 cm³, 16 ans), A2 (35 kW, 18 ans), et la passerelle vers A.",
        },
        {
          icon: Award,
          accent: '#0ea5e9',
          kicker: 'Valable à vie',
          title: "Une fois, pour toujours",
          body:
            "Tu ne la repasseras jamais. Conserve <strong>précieusement l'original papier</strong> — il te sera demandé des années plus tard pour chaque inscription à un permis.",
        },
      ],
      cardsCols: 3,
    },

    {
      part: 'Partie 2 — Le contenu',
      title: "Les 5 thèmes évalués à l'ASSR 2",
      intro:
        "L'ASSR 2 va <strong>plus loin que l'ASSR 1</strong>. On quitte la posture de « passager / piéton / cycliste » pour aborder la responsabilité de futur conducteur. <strong>20 questions, 30 minutes, 10/20 minimum</strong>.",
      cards: [
        {
          icon: Users,
          accent: '#0ea5e9',
          kicker: 'Thème 1',
          title: 'Le partage de la route',
          body:
            "Comment cohabiter avec tous les usagers : piétons, cyclistes, motards, voitures, camions. Angles morts, dépassements, sas vélo, couloirs bus. L'anticipation et la courtoisie.",
        },
        {
          icon: Eye,
          accent: '#16a34a',
          kicker: 'Thème 2',
          title: 'Signalisation & priorités',
          body:
            "Les grands panneaux à connaître (danger, interdiction, obligation, indication), les règles de priorité à droite, les priorités aux intersections, les feux tricolores et leurs exceptions.",
        },
        {
          icon: Wine,
          accent: '#cf2031',
          kicker: 'Thème 3',
          title: 'Alcool, stups, fatigue',
          body:
            "Les seuils légaux (<strong>0,2 g/L</strong> pour jeune permis, 0,5 g/L pour permis confirmé), l'effet des <strong>stupéfiants</strong>, la <strong>fatigue qui tue autant que l'alcool</strong>, les médicaments qui altèrent la vigilance.",
        },
        {
          icon: Scale,
          accent: '#7c3aed',
          kicker: 'Thème 4',
          title: 'Responsabilité',
          body:
            "La différence entre <strong>responsabilité civile</strong> (indemniser les victimes) et <strong>pénale</strong> (sanctions). L'importance de l'assurance. Conduire sans assurance = délit grave (jusqu'à 3 750 € d'amende).",
        },
        {
          icon: HeartPulse,
          accent: '#f59e0b',
          kicker: 'Thème 5',
          title: 'Face à un accident',
          body:
            "Les gestes PAS approfondis. Le témoin n°1 est souvent… le passager. Savoir décrire précisément à l'opérateur du 112. Ne jamais retirer le casque d'un motard blessé.",
        },
      ],
      cardsCols: 2,
    },

    {
      part: 'Partie 3 — La méthode',
      title: "Comment viser 15/20 (et pas juste 10)",
      intro:
        "10/20 suffit pour valider, mais viser plus haut te donne <strong>une vraie culture sécurité routière</strong> qui te servira 50 ans. Voici la méthode ECSR.",
      compareCards: [
        {
          accent: '#16a34a',
          kicker: '✓ Plan d\'attaque',
          title: "4 semaines de préparation",
          intro: "Un rythme simple qui marche :",
          points: [
            { ok: true, text: "<strong>Semaine 1</strong> — lire les 5 thèmes + s'entraîner sur 10 questions/jour site officiel" },
            { ok: true, text: "<strong>Semaine 2</strong> — refaire les questions ratées + regarder les vidéos Sécurité routière sur YouTube" },
            { ok: true, text: "<strong>Semaine 3</strong> — passer un test blanc complet (simuler les conditions réelles : 30 min chrono)" },
            { ok: true, text: "<strong>Semaine 4</strong> — repasser 2-3 tests blancs + discussion en famille sur les pièges" },
            { ok: true, text: "Observer ses propres trajets : comment conduit un parent, pourquoi il anticipe tel feu…" },
          ],
        },
        {
          accent: '#cf2031',
          kicker: '✗ À éviter absolument',
          title: "Les erreurs fatales",
          intro: "Ce qui fait échouer même les bons élèves :",
          points: [
            { ok: false, text: "Découvrir le format la veille (aucune préparation = échec probable)" },
            { ok: false, text: "Copier les « corrigés » trouvés en ligne — les questions sont tirées au hasard dans une base" },
            { ok: false, text: "Ne lire que la moitié de la question (piège : plusieurs bonnes réponses à cocher)" },
            { ok: false, text: "Négliger le thème « Responsabilité » (souvent jugé moins fun, pourtant souvent piégeux)" },
            { ok: false, text: "Arriver stressé le jour J faute de sommeil — ça change vraiment le score" },
          ],
          footer: "Règle d'or : un test blanc par semaine pendant un mois = objectif 15/20 atteignable.",
        },
      ],
    },

    {
      part: 'Partie 4 — Le jour J',
      title: "Dérouler l'examen en sérénité",
      cards: [
        {
          icon: Clock,
          accent: '#0ea5e9',
          kicker: 'Durée',
          title: '30 minutes',
          body:
            "20 questions en 30 minutes = 1'30 par question. Tu as le temps. Pas besoin de te précipiter.",
        },
        {
          icon: BookOpen,
          accent: '#16a34a',
          kicker: 'Format',
          title: 'Vidéos & images',
          body:
            "Chaque question est accompagnée d'une <strong>courte vidéo (≈ 20 sec)</strong> ou d'une image. Observe bien avant de répondre. Plusieurs bonnes réponses possibles sur certaines questions.",
        },
        {
          icon: Target,
          accent: '#cf2031',
          kicker: 'Réussite',
          title: '10/20 minimum',
          body:
            "10 bonnes réponses suffisent. Si tu échoues, <strong>session de rattrapage</strong> organisée au collège. Dernière chance : passer l'ASR au GRETA plus tard.",
        },
      ],
      cardsCols: 3,
    },

    {
      part: 'Partie 5 — Et après ?',
      title: "L'ASSR 2 obtenue : la suite logique",
      callouts: [
        {
          icon: Bike,
          accent: '#cf2031',
          body:
            "<strong>Tu veux passer le permis moto A1 dès 16 ans ?</strong> Direction le <strong>Code moto (ETM)</strong> — 40 questions spécifiques deux-roues. Sur CODE 2 ROUES, retrouve nos <strong>dossiers pédagogiques moto</strong> et nos <strong>livrets plateau &amp; circulation</strong> pour ancrer les notions clés.",
        },
        {
          icon: Car,
          accent: '#16a34a',
          body:
            "<strong>Tu veux passer le permis voiture en conduite accompagnée ?</strong> Dès 15 ans, tu peux commencer. L'ASSR 2 sera demandée à l'inscription chez ton auto-école.",
        },
        {
          icon: AlertTriangle,
          accent: '#7c3aed',
          body:
            "<strong>Tu as perdu ton attestation ?</strong> Contacte le secrétariat de ton ancien collège — ils archivent les originaux. Sinon, l'<strong>ASR au GRETA</strong> remplace l'ASSR 2 pour adulte.",
        },
      ],
    },
  ],

  quiz: [
    {
      id: 'q1',
      question: "L'ASSR 2 est obligatoire pour quel permis ?",
      options: [
        'Uniquement le permis moto',
        'Uniquement le permis voiture',
        'Tous les permis (pour les jeunes nés après 1987)',
        'Aucun, elle est optionnelle',
      ],
      correctIndex: 2,
      explain:
        "L'ASSR 2 (ou ASR pour adultes) est obligatoire à la première inscription à N'IMPORTE QUEL permis pour les jeunes nés après le 31 décembre 1987 : A1, A2, B, BE, poids lourd…",
    },
    {
      id: 'q2',
      question: "Quel est le seuil d'alcoolémie pour un jeune permis (moins de 3 ans) ?",
      options: [
        '0,5 g/L de sang',
        '0,2 g/L de sang',
        '0,8 g/L de sang',
        "Aucune tolérance (0 g/L)",
      ],
      correctIndex: 1,
      explain:
        "0,2 g/L de sang pour les jeunes permis (moins de 3 ans d'ancienneté, ou 2 ans en conduite accompagnée). Au-delà : retrait de 6 points + amende. Pour info : 0,5 g/L pour les permis confirmés.",
    },
    {
      id: 'q3',
      question: "Conduire sans assurance, c'est :",
      options: [
        "Une simple contravention",
        "Un délit pénal (jusqu'à 3 750 € d'amende)",
        "Toléré si c'est un prêt de voiture",
        "Sans conséquence juridique",
      ],
      correctIndex: 1,
      explain:
        "Conduire sans assurance est un DÉLIT pénal (article L324-2 du Code de la route). Peine : jusqu'à 3 750 € d'amende + suspension de permis + confiscation possible du véhicule. En cas d'accident, les victimes sont remboursées… par TOI (ta vie entière).",
    },
    {
      id: 'q4',
      question: "Face à un motard blessé sur le sol, que faut-il JAMAIS faire ?",
      options: [
        'Appeler le 112',
        'Mettre un gilet jaune pour te protéger',
        'Retirer son casque pour « qu\'il respire »',
        "Couper le contact de sa moto",
      ],
      correctIndex: 2,
      explain:
        "NE JAMAIS retirer le casque d'un motard blessé : tu risques d'aggraver une fracture cervicale. Les secours ont des techniques à 2 personnes pour le faire si nécessaire. Ton rôle : protéger la zone, alerter le 112, rassurer.",
    },
    {
      id: 'q5',
      question: "Tu as perdu ton attestation ASSR 2, que faire ?",
      options: [
        'Impossible de passer un permis, c\'est fichu',
        "Contacter le secrétariat du collège où tu l'as passée",
        "Déclarer la perte à la gendarmerie",
        "La racheter sur Internet",
      ],
      correctIndex: 1,
      explain:
        "Les collèges archivent les originaux. Contacte le secrétariat, même si tu n'y es plus scolarisé depuis 10 ans. Si le collège a fermé, adresse-toi au rectorat de l'académie. Alternative : passer l'ASR dans un GRETA.",
    },
  ],

  ctaFinal: {
    kicker: 'Prêt pour la suite ?',
    title: "ASSR 2 → permis moto : le parcours complet",
    body:
      "L'ASSR 2 en poche, tu peux te lancer dans le Code moto (ETM) en passant par ton auto-école ou un éditeur spécialisé. Pour comprendre les enjeux et t'entraîner en douceur, ouvre nos dossiers magazine et nos livrets plateau &amp; circulation.",
    primary: {
      label: "Voir le hub ASSR",
      to: '/assr',
    },
    secondary: {
      label: "Explorer les dossiers magazine",
      to: '/magazine',
    },
  },

  etm: {
    primary: 'L',
    secondary: ['D', 'S'],
    label: 'Conducteur, responsabilité & premiers secours',
    cta: 'Réviser le thème « Le conducteur »',
  },
};
